// src/pages/Authentication/UserProfile/AvatarUploadModal.tsx
// Modal for uploading user avatar

import React, { useState } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Label,
  Input,
  Progress,
  Alert,
} from 'reactstrap';
import { useDispatch } from 'react-redux';
import { uploadProfileAvatar } from 'slices/thunks';
import { AvatarUploadModalProps } from './types';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('authentication');

const AvatarUploadModal: React.FC<AvatarUploadModalProps> = ({
  isOpen,
  toggle,
  currentAvatar,
  userName,
  userId,
  onUploadSuccess,
}) => {
  const dispatch = useDispatch<any>();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string>('');

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError(st('vui_long_chon_file_anh_jpg_text'));
      return;
    }

    // Validate file size (2MB)
    if (file.size > 2 * 1024 * 1024) {
      setError(st('kich_thuoc_file_khong_duoc_vuot_text'));
      return;
    }

    setError('');
    setSelectedFile(file);

    // Create preview URL
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

    const handleUpload = async () => {
    if (!selectedFile) return;

    setUploading(true);
    setUploadProgress(0);

    try {
      // Simulate upload progress for better UX
      const interval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 90) {
            clearInterval(interval);
            return prev;
          }
          return prev + 10;
        });
      }, 200);

            // Validate userId
            if (!userId) {
              throw new Error(st('khong_tim_thay_thong_tin_nguoi_message'));
            }

            // Convert userId to number if it's string
            const userIdNum = typeof userId === 'string' ? parseInt(userId, 10) : userId;

            // Upload avatar using Redux thunk
            const resultAction = await dispatch(uploadProfileAvatar({ userId: userIdNum, file: selectedFile }));
      clearInterval(interval);
      setUploadProgress(100);

      if (uploadProfileAvatar.fulfilled.match(resultAction)) {
        // Success - avatarUrl is in payload
        const avatarUrl = resultAction.payload;
        onUploadSuccess(avatarUrl);
        
        // Reset and close
        setTimeout(() => {
          resetModal();
          toggle();
        }, 500);
      } else {
        // Handle rejection
        throw new Error(resultAction.payload as string);
      }
    } catch (err: any) {
      setError(err?.message || st('co_loi_xay_ra_khi_tai_text'));
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const resetModal = () => {
    setSelectedFile(null);
    setPreviewUrl('');
    setUploading(false);
    setUploadProgress(0);
    setError('');
  };

  const handleToggle = () => {
    if (!uploading) {
      resetModal();
      toggle();
    }
  };

  const displayAvatar = previewUrl || currentAvatar;

  return (
    <Modal isOpen={isOpen} toggle={handleToggle} centered>
      <ModalHeader toggle={handleToggle}>
        <i className="ri-camera-line me-2"></i>
        {st('doi_anh_dai_dien_title')}
      </ModalHeader>

      <ModalBody>
        {/* Current/Preview Avatar */}
        <div className="text-center mb-3">
          <img
            src={displayAvatar}
            alt={userName}
            className="rounded-circle"
            style={{
              width: '150px',
              height: '150px',
              objectFit: 'cover',
              border: '3px solid var(--vz-primary)',
            }}
          />
        </div>

        {/* File Input */}
        <div className="mb-3">
          <Label htmlFor="avatar-file" className="form-label">
            {st('chon_anh_moi_label')}
          </Label>
          <Input
            type="file"
            id="avatar-file"
            accept="image/*"
            onChange={handleFileChange}
            disabled={uploading}
          />
          <small className="text-muted">
            {st('dinh_dang_jpg_png_kich_thuoc_hint')}
          </small>
        </div>

        {/* Error Alert */}
        {error && (
          <Alert color="danger" className="mb-3">
            <i className="ri-error-warning-line me-2"></i>
            {error}
          </Alert>
        )}

        {/* Upload Progress */}
        {uploading && (
          <div className="mb-3">
            <div className="d-flex justify-content-between mb-1">
              <small className="text-muted">{st('dang_tai_len_hint')}</small>
              <small className="text-muted">{uploadProgress}%</small>
            </div>
            <Progress value={uploadProgress} color="primary" />
          </div>
        )}
      </ModalBody>

      <ModalFooter>
        <Button color="light" onClick={handleToggle} disabled={uploading}>
          <i className="ri-close-line me-1"></i>
          {st('huy_button')}
        </Button>
        <Button
          color="primary"
          onClick={handleUpload}
          disabled={!selectedFile || uploading}
        >
          {uploading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2"></span>
              {st('dang_tai_len_hint')}
            </>
          ) : (
            <>
              <i className="ri-upload-2-line me-1"></i>
              {st('luu_anh_text')}
            </>
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default AvatarUploadModal;